import { clamp } from "./math";

/**
 * A value the UI (or the Conductor) writes as `target`, which `current` follows
 * over `bars` bars. Musical parameters never jump: a density change that lands
 * instantly reads as an edit, not as music.
 */
export interface SmoothedScalar {
  current: number;
  target: number;
  /** Bars to travel the full distance. 1 means "arrive next bar". */
  bars: number;
}

export function createSmoothed(value: number, bars: number): SmoothedScalar {
  return { current: value, target: value, bars: Math.max(1, bars) };
}

/** Moves `current` one bar's worth towards `target`. Pure in spirit, mutating for GC. */
export function stepSmoothed(scalar: SmoothedScalar): void {
  const bars = Math.max(1, scalar.bars);
  const delta = scalar.target - scalar.current;
  if (Math.abs(delta) < 1e-4) {
    scalar.current = scalar.target;
    return;
  }
  scalar.current = clamp(scalar.current + delta / bars, 0, 1);
}

/** Sets the target, leaving `current` to catch up. */
export function setTarget(scalar: SmoothedScalar, target: number): void {
  scalar.target = clamp(target, 0, 1);
}

/** Jumps both values. Used by reset, never by ordinary parameter changes. */
export function snapSmoothed(scalar: SmoothedScalar, value: number): void {
  const v = clamp(value, 0, 1);
  scalar.current = v;
  scalar.target = v;
}

/**
 * Slow, non-repeating drift built from incommensurable sine periods.
 *
 * Used by the Conductor so energy and density wander a little on their own.
 * The periods are deliberately not multiples of each other, so the sum does not
 * repeat on any musically obvious cycle.
 */
export function slowDrift(bar: number, phase: number): number {
  return (
    0.6 * Math.sin((bar / 23) * Math.PI * 2 + phase) +
    0.3 * Math.sin((bar / 37) * Math.PI * 2 + phase * 1.7) +
    0.1 * Math.sin((bar / 61) * Math.PI * 2 + phase * 0.3)
  );
}
